<script setup lang="ts">
import type { JobCarouselItem } from '@/composables/useJobCarousel';

import { toRef } from 'vue';

import JobCarouselNavigation from '@/components/footers/JobCarouselNavigation.vue';
import SimpleLoader from '@/components/loaders/SimpleLoader.vue';
import JobEditContentSection from '@/components/sections/JobEditContentSection.vue';
import { useJobEditCarouselController } from '@/composables/jobEdit/useJobEditCarouselController';

export interface Props {
  jobId?: number;
  jobDate?: string;
  jobs?: JobCarouselItem[];
  onDataChanged?: () => Promise<void>;
}
const props = withDefaults(defineProps<Props>(), {
  jobId: undefined,
  jobDate: undefined,
  jobs: () => [],
  onDataChanged: undefined,
});

const emit = defineEmits<{
  cancelBtnClick: [];
}>();

const {
  carousel,
  slideAnimation,
  showInitialLoader,
  showContentLoader,
  showFooter,
  isNavigationDisabled,
  handlePreviousClick,
  handleNextClick,
} = useJobEditCarouselController({
  jobs: toRef(props, 'jobs'),
  jobId: toRef(props, 'jobId'),
  jobDate: toRef(props, 'jobDate'),
  onDataChanged: toRef(props, 'onDataChanged'),
});

function handleCancelClick(): void {
  emit('cancelBtnClick');
}
</script>

<template>
  <div :class="$style.jobEditDialog" data-testid="jobEditDialog">
    <div v-if="showInitialLoader" :class="$style.loaderWrapper">
      <SimpleLoader :size="64" :width="6" />
    </div>
    <div v-else :class="$style.contentWrapper">
      <v-sheet :class="$style.jobEditSheet">
        <div
          :class="[
            $style.carouselSlide,
            {
              'job-edit-slide-hidden': slideAnimation.isSlideHidden.value,
            },
            slideAnimation.activeAnimationClass.value,
          ]"
        >
          <JobEditContentSection
            show-close-btn
            @close-btn-click="handleCancelClick"
          />
        </div>

        <div v-if="showContentLoader" class="job-edit-content-loader-overlay">
          <SimpleLoader :size="48" :width="5" />
        </div>
      </v-sheet>
    </div>
    <JobCarouselNavigation
      v-if="showFooter"
      :class="$style.footer"
      :previous-job-id="carousel.previousJob.value?.jobId"
      :current-job-id="carousel.currentJob.value?.jobId"
      :next-job-id="carousel.nextJob.value?.jobId"
      :position="carousel.position.value"
      :can-navigate="carousel.canNavigate.value"
      :disabled="isNavigationDisabled"
      @previous-click="handlePreviousClick"
      @next-click="handleNextClick"
    />
  </div>
</template>

<style module lang="scss">
@use '@/styles/colors';

.jobEditDialog {
  display: flex;
  flex-direction: column;
  min-height: 90vh;
  max-height: 90vh;
  width: 100%;
  max-width: 1920px;
  margin: 0 auto;
}

.loaderWrapper {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: colors.$color-white;
  border-radius: 8px 8px 0 0;
}

.contentWrapper {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.jobEditSheet {
  background-color: colors.$color-white;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  border-radius: 8px 8px 0 0;
  border: 0;
  position: relative;
}

.carouselSlide {
  width: 100%;
}

.footer {
  flex-shrink: 0;
}
</style>

<style lang="scss">
@use '@/styles/colors';
@use '@/styles/jobEditCarouselAnimations';

.job-edit-slide-hidden {
  opacity: 0;
}

.job-edit-content-loader-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(colors.$color-white, 0.7);
  pointer-events: none;
}
</style>
